import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { createBackOfficeSeed } from "./backofficeData";

const STORAGE_KEY = "Jusstools_backoffice_v1";

const BackOfficeContext = createContext(null);

function readInitialState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return createBackOfficeSeed();
    }

    const parsed = JSON.parse(raw);
    const seed = createBackOfficeSeed();

    return {
      ...seed,
      orders: Array.isArray(parsed.orders) ? parsed.orders : seed.orders,
      notifications: Array.isArray(parsed.notifications) ? parsed.notifications : seed.notifications,
    };
  } catch (error) {
    return createBackOfficeSeed();
  }
}

export function BackOfficeProvider({ children }) {
  const [state, setState] = useState(readInitialState);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        orders: state.orders,
        notifications: state.notifications,
      })
    );
  }, [state.orders, state.notifications]);

  const actions = useMemo(
    () => ({
      updateOrder(orderId, updater) {
        setState((current) => ({
          ...current,
          orders: current.orders.map((order) =>
            order.id === orderId ? updater(order) : order
          ),
        }));
      },
      updateOrderStatus(orderId, status) {
        setState((current) => ({
          ...current,
          orders: current.orders.map((order) => {
            if (order.id !== orderId) {
              return order;
            }

            const updatedAt = new Date().toISOString();
            return {
              ...order,
              status,
              updatedAt,
              activity: [
                {
                  id: `activity-${order.id}-${Date.now()}`,
                  label: `Status changed to ${status}`,
                  timestamp: updatedAt,
                },
                ...order.activity,
              ],
            };
          }),
          notifications: [
            {
              id: `notice-${Date.now()}`,
              title: "Status updated",
              body: `${orderId} moved to ${status}`,
              unread: true,
              timestamp: new Date().toISOString(),
            },
            ...current.notifications,
          ],
        }));
      },
      addOrderNote(orderId, note) {
        const timestamp = new Date().toISOString();
        setState((current) => ({
          ...current,
          orders: current.orders.map((order) => {
            if (order.id !== orderId) {
              return order;
            }

            return {
              ...order,
              notes: [
                {
                  id: `note-${Date.now()}`,
                  author: note.author,
                  timestamp,
                  body: note.body,
                },
                ...order.notes,
              ],
              activity: [
                {
                  id: `activity-${order.id}-${Date.now()}`,
                  label: "Internal note added",
                  timestamp,
                },
                ...order.activity,
              ],
              updatedAt: timestamp,
            };
          }),
        }));
      },
      assignCourier(orderId, courier) {
        const timestamp = new Date().toISOString();
        setState((current) => ({
          ...current,
          orders: current.orders.map((order) => {
            if (order.id !== orderId) {
              return order;
            }

            return {
              ...order,
              courier: {
                ...order.courier,
                ...courier,
              },
              activity: [
                {
                  id: `activity-${order.id}-${Date.now()}`,
                  label: `Courier changed to ${courier.name}`,
                  timestamp,
                },
                ...order.activity,
              ],
              updatedAt: timestamp,
            };
          }),
        }));
      },
      markNotificationRead(notificationId) {
        setState((current) => ({
          ...current,
          notifications: current.notifications.map((notification) =>
            notification.id === notificationId
              ? { ...notification, unread: false }
              : notification
          ),
        }));
      },
      markAllNotificationsRead() {
        setState((current) => ({
          ...current,
          notifications: current.notifications.map((notification) => ({
            ...notification,
            unread: false,
          })),
        }));
      },
      refreshSeed() {
        setState(createBackOfficeSeed());
      },
    }),
    []
  );

  const value = useMemo(() => ({ ...state, ...actions }), [state, actions]);

  return (
    <BackOfficeContext.Provider value={value}>
      {children}
    </BackOfficeContext.Provider>
  );
}

export function useBackOffice() {
  const context = useContext(BackOfficeContext);

  if (!context) {
    throw new Error("useBackOffice must be used within BackOfficeProvider");
  }

  return context;
}

